export const resumeUrl = '/resume.pdf';

export const socialLinks = [
  { label: 'GitHub', url: 'https://github.com/mhdnihas' },
  { label: 'LinkedIn', url: 'https://www.linkedin.com/in/muhammed-nihas-ai/' },
  { label: 'GitLab', url: 'https://gitlab.com/mhdnihas' },
  { label: 'LeetCode', url: 'https://leetcode.com/u/muhammednihas2218/' },
];

export const navigation = [
  { label: 'About', href: '#about' },
  { label: 'Experience', href: '#experience' },
  { label: 'Work', href: '#professional-work' },
  { label: 'Projects', href: '#projects' },
  { label: 'Open source', href: '#open-source' },
  { label: 'Skills', href: '#skills' },
  { label: 'Education', href: '#education' },
  { label: 'Contact', href: '#contact' },
];

export const professionalWork = [
  {
    number: '01',
    title: 'AI Infrastructure Support Agent',
    category: 'Multi-agent support platform',
    problem: 'Infrastructure requests span ticketing, inventory, and account systems, where stale data or unverified actions create risk.',
    summary: 'A stateful support system that routes infrastructure requests to specialized agents and tools for ticketing, inventory lookup, and account verification.',
    contribution: 'Built LangGraph orchestration, persistent conversation state, tool validation, and an evaluation workflow for reliable agent handoffs.',
    workflow: ['User request', 'LangGraph routing', 'Verified tool calls', 'Observed response'],
    details: [
      'Coordinated ticketing and infrastructure inventory agents with shared session context and MongoDB checkpointers.',
      'Added fresh API lookups, account checks, and confirmation gates before sensitive actions.',
      'Used Langfuse traces and agent evaluations to inspect tool use and response quality.',
    ],
    stack: ['LangGraph', 'FastAPI', 'MongoDB', 'Langfuse', 'NeMo Guardrails'],
  },
  {
    number: '02',
    title: 'M-Vault',
    category: 'Document intelligence platform',
    problem: 'Large, mixed-format document collections need processing that stays responsive and recovers from interrupted jobs.',
    summary: 'An asynchronous document processing and RAG platform for extraction, summarization, classification, and question answering across common file formats.',
    contribution: 'Designed ingestion and retrieval workflows, queue-based processing, and recovery for multi-user document jobs.',
    workflow: ['Documents', 'Celery processing', 'Embeddings + retrieval', 'Answers & summaries'],
    details: [
      'Processed PDF, DOCX, PPTX, image, and text inputs with page-level batching and retryable workers.',
      'Combined query decomposition, semantic retrieval, and reranking for document questions.',
      'Tracked persistent job state and stale tasks so workers could recover after interruption.',
    ],
    stack: ['FastAPI', 'Celery', 'Redis', 'MongoDB', 'ChromaDB'],
  },
  {
    number: '03',
    title: 'M for Coding',
    category: 'Agentic coding assistant',
    problem: 'Developers need coding assistance in both terminal and editor workflows without leaving model access uncontrolled.',
    summary: 'An internal AI coding product delivered through CLI and editor workflows with model integration and authenticated access.',
    contribution: 'Led implementation of the coding CLI and extension workflow, including middleware, authentication, and device validation.',
    workflow: ['Developer prompt', 'CLI / editor', 'Auth middleware', 'Coding model'],
    details: [
      'Integrated a coding model into developer-facing CLI and VS Code extension flows.',
      'Implemented authentication middleware and device validation around model access.',
      'Kept the product focused on coding assistance and reliable developer workflows.',
    ],
    stack: ['Python', 'VS Code', 'CLI', 'Qwen3-Coder', 'Authentication'],
  },
  {
    number: '04',
    title: 'Revenue Intelligence Platform',
    category: 'Multi-agent analytics',
    problem: 'Revenue planning requires answers across invoices, costs, capacity, forecasts, and external context held in separate sources.',
    summary: 'A planning system that brings SQL analytics, forecasting, document retrieval, and web research into one revenue analysis workflow.',
    contribution: 'Architected agent routing and scenario analysis for revenue, cost, capacity, and gross-margin questions.',
    workflow: ['Planning question', 'Specialized agents', 'Data + forecasts', 'Scenario insight'],
    details: [
      'Used schema-aware natural language to SQL for financial analysis.',
      'Combined historical data with forecasting and scenario inputs for planning.',
      'Connected document retrieval and web research to the same orchestrated workflow.',
    ],
    stack: ['LangGraph', 'LangChain', 'SQL', 'ChromaDB', 'Forecasting'],
  },
];

export const personalProjects = [
  {
    number: '01',
    title: 'MediFlow AI',
    category: 'Featured · Agentic healthcare',
    problem: 'Appointment and doctor discovery spans multiple roles and live scheduling data.',
    solution: 'A full-stack healthcare platform with a LangGraph receptionist that routes requests to the right tools and models.',
    contribution: 'Designed the agent workflow, FastAPI and MongoDB backend, JWT-secured role portals, and voice interaction flow.',
    workflow: ['Patient request', 'ReAct agent', 'Doctor discovery', 'Appointment booking'],
    stack: ['LangGraph', 'FastAPI', 'MongoDB', 'Next.js', 'Web Speech API'],
    featured: true,
  },
  {
    number: '02',
    title: 'Indian Constitution Chatbot',
    category: 'RAG · Question answering',
    problem: 'Finding relevant constitutional information in long source documents is difficult.',
    solution: 'A retrieval pipeline finds supporting passages before an LLM drafts an answer.',
    contribution: 'Built document processing, FAISS retrieval, Hugging Face embeddings, and a FastAPI interface.',
    workflow: ['Question', 'Embedding search', 'Relevant passages', 'Generated answer'],
    stack: ['RAG', 'FAISS', 'LangChain', 'FastAPI', 'Hugging Face'],
    source: 'https://github.com/mhdnihas/Indian-Constitution-Bot',
  },
  {
    number: '03',
    title: 'Meal Demand Forecasting',
    category: 'Time series · Applied ML',
    problem: 'Variable meal demand makes inventory and staffing decisions harder.',
    solution: 'A forecasting workflow explores historical orders and related signals, then presents predictions in an interactive app.',
    contribution: 'Developed the data pipeline, model experiments, Streamlit interface, and Tableau analysis.',
    workflow: ['Order history', 'Feature preparation', 'LSTM forecast', 'Planning dashboard'],
    stack: ['Python', 'TensorFlow', 'LSTM', 'Streamlit', 'Tableau'],
    source: 'https://github.com/mhdnihas/Meal-Demand-Forecasting',
  },
  {
    number: '04',
    title: 'NASA Turbofan RUL Prediction',
    category: 'Predictive maintenance · ML',
    problem: 'Engine sensor readings change across operating conditions, making remaining useful life hard to estimate.',
    solution: 'A machine learning workflow analyzes NASA C-MAPSS sensor data and predicts remaining useful life.',
    contribution: 'Performed exploratory analysis, feature engineering, and Random Forest modeling.',
    workflow: ['Sensor data', 'Feature engineering', 'Random Forest', 'RUL estimate'],
    stack: ['Python', 'Scikit-learn', 'Random Forest', 'Time series'],
    source: 'https://github.com/mhdnihas/NASA-Predictive-Engine-Maintenance',
  },
];

export const additionalProjects = [
  {
    title: 'Amazon Review Sentiment & MLOps',
    summary: 'An earlier sentiment analysis system with FastAPI endpoints, experiment tracking, and data drift monitoring.',
    source: 'https://github.com/mhdnihas/Amazon-Customer-Review-Sentiment-and-Monitoring-MLOps',
  },
];

export const skillCategories = [
  { title: 'Agentic AI & LLMs', skills: ['LangGraph', 'LangChain', 'LLMs', 'Multi-agent orchestration', 'MCP', 'VLMs'] },
  { title: 'RAG & retrieval', skills: ['Embeddings', 'Vector search', 'ChromaDB', 'Query decomposition', 'Reranking', 'Hybrid search'] },
  { title: 'Model serving & inference', skills: ['vLLM', 'llama.cpp', 'NVIDIA NIM', 'Quantization', 'Inference optimization'] },
  { title: 'Machine learning', skills: ['PyTorch', 'TensorFlow', 'Scikit-learn', 'Hugging Face', 'Time series forecasting'] },
  { title: 'Backend & APIs', skills: ['Python', 'FastAPI', 'Django', 'REST APIs', 'Celery', 'Asynchronous processing'] },
  { title: 'Data systems', skills: ['MongoDB', 'PostgreSQL', 'MySQL', 'Redis'] },
  { title: 'Evaluation & observability', skills: ['Agent evaluation', 'LLM-as-a-judge', 'Langfuse', 'NeMo Guardrails', 'Reliability testing'] },
  { title: 'Cloud & deployment', skills: ['Docker', 'Azure', 'GCP', 'AWS S3', 'MLflow'] },
];
